// ---------------------------------------------------------------------------
// MissedJobsReview
//
// Staff-only list of jobs whose date passed without being completed.
//   mode="modal"  -> the popup on Home (opens once a day until handled)
//   mode="inline" -> the "Missed" tab on Needs Scheduling
//
// Every action asks WHY the job was missed and writes it to job.missedLog,
// so the office can see patterns later (weather, materials, no tech...).
// All row math and Firestore writes live in utils/missedJobs.js.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getAuth } from "firebase/auth";
import { blockIfViewingAs } from "../utils/viewAs";
import {
  MISSED_REASONS,
  rescheduleMissed,
  sendMissedToNeedsScheduling,
  sendMissedToReadyToInvoice,
  skipMissedVisit,
  todayKey,
} from "../utils/missedJobs";
import { HowToButton } from "./HowToGuide";
import "./MissedJobsReview.css";

const AGE_FILTERS = [
  { id: "all", label: "All", test: () => true },
  { id: "week", label: "1–7 days", test: (d) => d <= 7 },
  { id: "month", label: "8–30 days", test: (d) => d > 7 && d <= 30 },
  { id: "quarter", label: "31–90 days", test: (d) => d > 30 && d <= 90 },
  { id: "old", label: "90+ days", test: (d) => d > 90 },
];

const TYPE_FILTERS = [
  { id: "all", label: "All types" },
  { id: "one", label: "One-time" },
  { id: "recurring", label: "Recurring" },
];

const ACTIONS = {
  reschedule: {
    label: "Reschedule",
    icon: "📅",
    title: "Reschedule",
    explain: (n) => `Move ${n} job${n === 1 ? "" : "s"} to a new date. Recurring jobs move only this visit — the rest of the series stays the same.`,
    needsReason: true,
  },
  needsScheduling: {
    label: "Needs Scheduling",
    icon: "🧩",
    title: "Send back to Needs Scheduling",
    explain: (n) => `Clear the date on ${n} job${n === 1 ? "" : "s"} so they show on Needs Scheduling and get booked again.`,
    needsReason: true,
    oneTimeOnly: true,
  },
  done: {
    label: "It was done",
    icon: "✅",
    title: "It was done → Ready to Invoice",
    explain: (n) => `Send ${n} job${n === 1 ? "" : "s"} to the Ready to Invoice list at the top of Home, where you finish each one into an invoice like a tech completion. Nothing is billed or emailed from here.`,
    needsReason: false,
  },
  skip: {
    label: "Skip visit",
    icon: "⏭",
    title: "Skip this visit",
    explain: (n) => `Skip ${n} recurring visit${n === 1 ? "" : "s"}. The series keeps going on its normal schedule.`,
    needsReason: true,
    recurringOnly: true,
  },
};

const formatDay = (key) => (key ? moment(key, "YYYY-MM-DD").format("ddd, MMM D, YYYY") : "");
const formatShort = (key) => (key ? moment(key, "YYYY-MM-DD").format("MMM D") : "");

const lateClass = (days) => {
  if (days > 30) return "mj-late mj-late--old";
  if (days > 7) return "mj-late mj-late--mid";
  return "mj-late";
};

const ACTION_LABELS = {
  rescheduled: "Rescheduled",
  needsScheduling: "Sent to Needs Scheduling",
  skipped: "Skipped",
  readyToInvoice: "Marked done",
};

const MissedJobsReview = ({
  rows = [],
  mode = "modal",
  open = true,
  onClose,
  onRemindLater,
  onSnoozeKeys,
  snoozedKeys = [],
  onResults,
  onOpenJob,
  staffName = "",
  pendingDoneCount = 0,
}) => {
  const isModal = mode === "modal";

  const [search, setSearch] = useState("");
  const [age, setAge] = useState("all");
  const [type, setType] = useState("all");
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const [action, setAction] = useState(null); // key of ACTIONS
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [skipOlder, setSkipOlder] = useState(true);
  const [busy, setBusy] = useState(null); // { done, total }
  const [message, setMessage] = useState(null); // { kind, text }

  const snoozedSet = useMemo(() => new Set(snoozedKeys), [snoozedKeys]);

  // Drop selections for rows that disappeared (handled elsewhere / reloaded).
  useEffect(() => {
    setSelected((prev) => {
      const live = new Set(rows.map((r) => r.key));
      const next = new Set([...prev].filter((k) => live.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const visibleRows = useMemo(() => {
    const ageTest = AGE_FILTERS.find((f) => f.id === age)?.test || (() => true);
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (isModal && !showSnoozed && snoozedSet.has(r.key)) return false;
      if (!ageTest(r.daysLate)) return false;
      if (type === "one" && r.recurring) return false;
      if (type === "recurring" && !r.recurring) return false;
      if (!term) return true;
      return [r.wo, r.siteName, r.city, r.serviceType, r.techs.join(" "), r.job.clientWorkOrder]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [rows, age, type, search, isModal, showSnoozed, snoozedSet]);

  const snoozedCount = useMemo(
    () => (isModal ? rows.filter((r) => snoozedSet.has(r.key)).length : 0),
    [rows, snoozedSet, isModal]
  );

  const ageCounts = useMemo(() => {
    const counts = {};
    AGE_FILTERS.forEach((f) => {
      counts[f.id] = rows.filter(
        (r) => f.test(r.daysLate) && !(isModal && !showSnoozed && snoozedSet.has(r.key))
      ).length;
    });
    return counts;
  }, [rows, isModal, showSnoozed, snoozedSet]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.key)), [rows, selected]);
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.key));

  const toggleRow = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleRows.forEach((r) => next.delete(r.key));
      else visibleRows.forEach((r) => next.add(r.key));
      return next;
    });

  // Which selected rows an action applies to, and which it leaves alone.
  const split = useMemo(() => {
    if (!action) return { apply: [], leave: [] };
    const def = ACTIONS[action];
    const apply = selectedRows.filter((r) => {
      if (def.oneTimeOnly && r.recurring) return false;
      if (def.recurringOnly && !r.recurring) return false;
      return true;
    });
    const leave = selectedRows.filter((r) => !apply.includes(r));
    return { apply, leave };
  }, [action, selectedRows]);

  const olderCount = useMemo(
    () => split.apply.reduce((sum, r) => sum + (r.recurring ? r.olderMissedKeys.length : 0), 0),
    [split]
  );

  const openAction = (key) => {
    setAction(key);
    setReason(key === "done" ? "Was done - never checked off" : "");
    setNote("");
    setNewDate("");
    // Older missed visits: clear them by default when moving/skipping. When
    // marking done, leave them alone by default - they may also need billing.
    setSkipOlder(key !== "done");
    setMessage(null);
  };

  const closeAction = () => {
    setAction(null);
    setMessage(null);
  };

  const today = todayKey();
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");

  const actionProblem = (() => {
    if (!action) return "";
    const def = ACTIONS[action];
    if (!split.apply.length) {
      if (def.oneTimeOnly) return "Only one-time jobs can go back to Needs Scheduling. Use Reschedule or Skip visit for recurring jobs.";
      if (def.recurringOnly) return "Only recurring visits can be skipped. Use Reschedule or Needs Scheduling for one-time jobs.";
      return "Select at least one job.";
    }
    if (action === "reschedule") {
      if (!newDate) return "Pick the new date.";
      if (newDate < today) return "The new date is in the past — pick today or later.";
    }
    if (def.needsReason && !reason) return "Pick why it was missed.";
    if (reason === "Other" && !note.trim()) return "Add a short note for “Other”.";
    return "";
  })();

  const runAction = async () => {
    if (actionProblem || busy) return;
    if (blockIfViewingAs()) return;

    const def = ACTIONS[action];
    const user = getAuth().currentUser;
    const opts = {
      reason,
      note,
      skipOlder,
      by: staffName || user?.displayName || user?.email || "",
      byUid: user?.uid || "",
    };

    const targets = split.apply;
    const results = [];
    const failures = [];
    setBusy({ done: 0, total: targets.length });

    for (let i = 0; i < targets.length; i += 1) {
      const row = targets[i];
      try {
        let result;
        if (action === "reschedule") result = await rescheduleMissed(row, newDate, opts);
        else if (action === "needsScheduling") result = await sendMissedToNeedsScheduling(row, opts);
        else if (action === "skip") result = await skipMissedVisit(row, opts);
        else if (action === "done") result = await sendMissedToReadyToInvoice(row, opts);
        if (result) results.push(result);
      } catch (err) {
        console.error("Missed job action failed:", row.key, err);
        failures.push(`${row.wo || row.siteName || row.jobId}: ${err?.message || err}`);
      }
      setBusy({ done: i + 1, total: targets.length });
    }

    if (results.length && onResults) onResults(results);

    setBusy(null);
    setSelected((prev) => {
      const next = new Set(prev);
      targets.forEach((r) => next.delete(r.key));
      return next;
    });

    if (failures.length) {
      setMessage({
        kind: "error",
        text: `${results.length} saved. ${failures.length} did not save: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`,
      });
    } else {
      setAction(null);
      setMessage({
        kind: "ok",
        text: `${def.title}: ${results.length} job${results.length === 1 ? "" : "s"} done.`,
      });
    }
  };

  const snoozeSelected = () => {
    if (!selectedRows.length || !onSnoozeKeys) return;
    onSnoozeKeys(selectedRows.map((r) => r.key));
    setSelected(new Set());
    setMessage({ kind: "ok", text: `${selectedRows.length} job${selectedRows.length === 1 ? "" : "s"} will come back tomorrow.` });
  };

  if (isModal && !open) return null;

  const body = (
    <div className={`mj ${isModal ? "mj--modal" : "mj--inline"}`} onClick={(e) => e.stopPropagation()}>
      {/* ---------- Header ---------- */}
      <div className="mj-head">
        <div>
          <h2 className="mj-title">
            <span aria-hidden="true">🚨</span> Missed Jobs
            <span className="mj-count">{rows.length}</span>
          </h2>
          <p className="mj-sub">
            {rows.length
              ? "These passed their date without being completed. Decide what happens to each one so it gets back on the schedule."
              : "Nothing missed. Every past job was completed, rescheduled or skipped."}
          </p>
          {pendingDoneCount > 0 && (
            <p className="mj-sub mj-sub--note">
              {pendingDoneCount} more {pendingDoneCount === 1 ? "was" : "were"} marked done and {pendingDoneCount === 1 ? "is" : "are"} waiting in Ready to Invoice.
            </p>
          )}
        </div>
        <div className="mj-head-actions">
          {/* Where to add everything, with links that open the right screen. */}
          <HowToButton topic={"missed-jobs"} onNavigate={isModal ? onClose : undefined} />
          {isModal && (
            <button type="button" className="mj-x" onClick={onRemindLater} aria-label="Close and remind me tomorrow">
              ✕
            </button>
          )}
        </div>
      </div>

      {rows.length > 0 && (
        <>
          {/* ---------- Filters ---------- */}
          <div className="mj-filters">
            <input
              className="mj-search"
              type="search"
              placeholder="Search WO, site, city, tech…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="mj-chips" role="group" aria-label="How late">
              {AGE_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`mj-chip ${age === f.id ? "is-on" : ""}`}
                  onClick={() => setAge(f.id)}
                >
                  {f.label} <span className="mj-chip-n">{ageCounts[f.id]}</span>
                </button>
              ))}
            </div>
            <select className="mj-select" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPE_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          {/* ---------- Selection + actions ---------- */}
          <div className="mj-bar">
            <label className="mj-selall">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} />
              <span>
                {allVisibleSelected ? "Unselect" : "Select"} all {visibleRows.length}
              </span>
            </label>
            <span className="mj-selcount">{selected.size ? `${selected.size} selected` : "Select jobs, then pick an action"}</span>
            <div className="mj-actions">
              {Object.entries(ACTIONS).map(([key, def]) => (
                <button
                  key={key}
                  type="button"
                  className={`mj-btn mj-btn--${key} ${action === key ? "is-on" : ""}`}
                  disabled={!selected.size || Boolean(busy)}
                  onClick={() => openAction(key)}
                >
                  <span aria-hidden="true">{def.icon}</span> {def.label}
                </button>
              ))}
              {isModal && (
                <button
                  type="button"
                  className="mj-btn mj-btn--later"
                  disabled={!selected.size || Boolean(busy)}
                  onClick={snoozeSelected}
                  title="Hide these until tomorrow"
                >
                  <span aria-hidden="true">⏰</span> Later
                </button>
              )}
            </div>
          </div>

          {/* ---------- Action step ---------- */}
          {action && (
            <div className="mj-step">
              <div className="mj-step-head">
                <strong>
                  {ACTIONS[action].icon} {ACTIONS[action].title}
                </strong>
                <button type="button" className="mj-link" onClick={closeAction} disabled={Boolean(busy)}>
                  Cancel
                </button>
              </div>
              <p className="mj-step-text">{ACTIONS[action].explain(split.apply.length)}</p>
              {split.leave.length > 0 && (
                <p className="mj-step-warn">
                  {split.leave.length} selected job{split.leave.length === 1 ? "" : "s"} will be left alone (
                  {ACTIONS[action].oneTimeOnly ? "recurring" : "one-time"}).
                </p>
              )}

              <div className="mj-step-grid">
                {action === "reschedule" && (
                  <label className="mj-field">
                    <span>New date</span>
                    <div className="mj-daterow">
                      <input type="date" min={today} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                      <button type="button" className="mj-link" onClick={() => setNewDate(tomorrow)}>
                        Tomorrow
                      </button>
                      <button
                        type="button"
                        className="mj-link"
                        onClick={() => setNewDate(moment().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD"))}
                      >
                        Next Monday
                      </button>
                    </div>
                    {split.apply.some((r) => r.spanDays > 0) && (
                      <small>Multi-day jobs keep their length.</small>
                    )}
                  </label>
                )}

                <label className="mj-field">
                  <span>
                    Why was it missed?{ACTIONS[action].needsReason ? "" : " (optional)"}
                  </span>
                  <select value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Choose a reason…</option>
                    {MISSED_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="mj-field mj-field--wide">
                  <span>Note {reason === "Other" ? "" : "(optional)"}</span>
                  <input
                    type="text"
                    value={note}
                    maxLength={300}
                    placeholder="Anything the next person should know"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
              </div>

              {olderCount > 0 && (
                <label className="mj-older">
                  <input type="checkbox" checked={skipOlder} onChange={(e) => setSkipOlder(e.target.checked)} />
                  <span>
                    Also mark the {olderCount} older missed visit{olderCount === 1 ? "" : "s"} on these recurring jobs as
                    skipped{action === "done" ? " (they will not go to Ready to Invoice)" : ""}. If unchecked, the next
                    older visit shows up here tomorrow.
                  </span>
                </label>
              )}

              <div className="mj-step-foot">
                {actionProblem ? <span className="mj-problem">{actionProblem}</span> : <span />}
                <button
                  type="button"
                  className="mj-go"
                  disabled={Boolean(actionProblem) || Boolean(busy)}
                  onClick={runAction}
                >
                  {busy
                    ? `Saving ${busy.done} of ${busy.total}…`
                    : `${ACTIONS[action].label} ${split.apply.length} job${split.apply.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          )}

          {message && <div className={`mj-msg mj-msg--${message.kind}`}>{message.text}</div>}

          {/* ---------- Rows ---------- */}
          <ul className="mj-list">
            {visibleRows.map((r) => {
              const isSel = selected.has(r.key);
              const isSnoozed = snoozedSet.has(r.key);
              return (
                <li key={r.key} className={`mj-row ${isSel ? "is-sel" : ""}`}>
                  <label className="mj-check">
                    <input type="checkbox" checked={isSel} onChange={() => toggleRow(r.key)} aria-label={`Select ${r.wo || r.siteName}`} />
                  </label>

                  <div className="mj-main" onClick={() => toggleRow(r.key)}>
                    <div className="mj-line1">
                      {r.wo && <span className="mj-wo">WO {r.wo}</span>}
                      <span className="mj-site">{r.siteName || "No site name"}</span>
                      {r.city && <span className="mj-city">{r.city}</span>}
                    </div>
                    <div className="mj-line2">
                      {r.serviceType && <span>{r.serviceType}</span>}
                      <span>{r.techs.length ? r.techs.join(", ") : "No tech assigned"}</span>
                      {r.recurring && (
                        <span className="mj-tag mj-tag--rec">
                          Recurring{r.olderMissedKeys.length ? ` · +${r.olderMissedKeys.length} older missed` : ""}
                        </span>
                      )}
                      {isSnoozed && <span className="mj-tag">Later</span>}
                    </div>
                    {r.lastLog && (
                      <div className="mj-history">
                        Missed before{r.timesMissedBefore > 1 ? ` ×${r.timesMissedBefore}` : ""}: {ACTION_LABELS[r.lastLog.action] || r.lastLog.action}
                        {r.lastLog.reason ? ` — ${r.lastLog.reason}` : ""}
                        {r.lastLog.at ? ` (${moment(r.lastLog.at).format("MMM D")})` : ""}
                      </div>
                    )}
                  </div>

                  <div className="mj-when">
                    <div className="mj-date" title={formatDay(r.missedDate)}>
                      {formatShort(r.missedDate)}
                      {r.missedEndDate !== r.missedDate ? ` – ${formatShort(r.missedEndDate)}` : ""}
                    </div>
                    <div className={lateClass(r.daysLate)}>
                      {r.daysLate} day{r.daysLate === 1 ? "" : "s"} late
                    </div>
                  </div>

                  <div className="mj-rowbtns">
                    {onOpenJob && (
                      <button type="button" className="mj-link" onClick={() => onOpenJob(r)}>
                        Open
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
            {!visibleRows.length && <li className="mj-empty">No jobs match these filters.</li>}
          </ul>

          {isModal && snoozedCount > 0 && (
            <button type="button" className="mj-link mj-snoozed" onClick={() => setShowSnoozed((v) => !v)}>
              {showSnoozed ? "Hide" : "Show"} {snoozedCount} job{snoozedCount === 1 ? "" : "s"} set for later
            </button>
          )}
        </>
      )}

      {isModal && (
        <div className="mj-foot">
          <span className="mj-foot-note">
            {rows.length ? "Closing brings this back tomorrow. The red Missed button on Home opens it anytime." : ""}
          </span>
          <div className="mj-foot-btns">
            {rows.length > 0 ? (
              <button type="button" className="mj-btn mj-btn--later" onClick={onRemindLater} disabled={Boolean(busy)}>
                ⏰ Remind me tomorrow
              </button>
            ) : (
              <button type="button" className="mj-btn" onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!isModal) return body;

  return (
    <div className="mj-overlay" onClick={busy ? undefined : onRemindLater}>
      {body}
    </div>
  );
};

/* Small red banner for the staff Home - always visible while jobs are missed. */
export const MissedJobsBanner = ({ count = 0, onOpen }) => {
  if (!count) return null;
  return (
    <button type="button" className="mj-banner" onClick={onOpen}>
      <span className="mj-banner-icon" aria-hidden="true">🚨</span>
      <span className="mj-banner-text">
        <strong>
          {count} missed job{count === 1 ? "" : "s"}
        </strong>{" "}
        passed their date without being completed
      </span>
      <span className="mj-banner-cta">Review</span>
    </button>
  );
};

export default MissedJobsReview;
